import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import { AlertTriangle, Mic, Music, Play, Repeat, Square, X } from 'lucide-react';
import type { PerformanceTake } from '../composition/performance';
import type { LiveComposerEvent } from '../../CreateMusicXml';
import {
  describeMidiNote,
  getTakeReviewNotes,
  summarizeTake,
  type TakeReviewNote,
} from '../composition/takeReview';
import './CompositionWorkspace.css';

const formatDuration = (durationMs: number) => {
  const totalSeconds = Math.round(durationMs / 1000);
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

const formatBytes = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export type TakeAudition = 'idle' | 'original' | 'edited';

/**
 * Review of what was actually played, next to what was written.
 *
 * A take is an immutable record of a performance, so nothing here edits it. The
 * panel exists to answer one question quickly: which notes did recognition get
 * wrong, and where are they in the score?
 */
export function TakeReviewPanel({
  open,
  takes,
  events,
  activeTakeId,
  audition,
  audioAvailable,
  onClose,
  onSelectTake,
  onRename,
  onPlayOriginal,
  onPlayEdited,
  onStop,
  onJumpToNote,
  onReplaceWithNewTake,
  onDeleteAudio,
  message,
}: {
  open: boolean;
  takes: PerformanceTake[];
  events: LiveComposerEvent[];
  activeTakeId?: string;
  audition: TakeAudition;
  /** False when the original recording is not on this device, e.g. after syncing. */
  audioAvailable: boolean;
  onClose: () => void;
  onSelectTake: (takeId: string) => void;
  onRename: (takeId: string, name: string) => void;
  onPlayOriginal: (take: PerformanceTake) => void;
  onPlayEdited: (take: PerformanceTake) => void;
  onStop: () => void;
  onJumpToNote: (note: TakeReviewNote) => void;
  onReplaceWithNewTake: (take: PerformanceTake) => void;
  onDeleteAudio: (take: PerformanceTake) => void;
  message?: string;
}) {
  const [draftName, setDraftName] = useState('');
  const activeTake = takes.find((take) => take.id === activeTakeId) ?? takes[0];
  useEffect(() => {
    setDraftName(activeTake?.name ?? '');
  }, [activeTake?.id, activeTake?.name]);

  const reviewNotes = useMemo(
    () => (activeTake ? getTakeReviewNotes(activeTake, events) : []),
    [activeTake, events],
  );
  const summaries = useMemo(() => takes.map(summarizeTake), [takes]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'composition-dialog' }}
    >
      <DialogTitle className="composition-dialog-heading">
        Takes
        <IconButton aria-label="Close takes" onClick={onClose}>
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent data-take-review>
        {takes.length === 0 ? (
          <p className="composition-muted" data-take-review-empty>
            Recorded takes appear here. Each one keeps the timing and touch of the original
            performance, so you can hear what you played after correcting the notes.
          </p>
        ) : (
          <>
            <div className="composition-take-list" role="list">
              {summaries.map((summary) => (
                <button
                  key={summary.id}
                  type="button"
                  role="listitem"
                  data-take-item={summary.id}
                  aria-pressed={summary.id === activeTake?.id}
                  className={`composition-take${summary.id === activeTake?.id ? ' is-active' : ''}`}
                  onClick={() => onSelectTake(summary.id)}
                >
                  <span className="composition-take-icon">
                    {summary.source === 'audio' ? <Mic size={16} /> : <Music size={16} />}
                  </span>
                  <span className="composition-take-body">
                    <span className="composition-take-name">{summary.name}</span>
                    <span className="composition-muted">
                      {formatDuration(summary.durationMs)} · {summary.noteCount} notes
                      {summary.hasAudio ? ` · ${formatBytes(summary.audioByteLength)} audio` : ''}
                      {summary.status === 'interrupted' ? ' · interrupted' : ''}
                    </span>
                  </span>
                  {summary.reviewCount > 0 && (
                    <span className="composition-take-flag" data-take-review-count={summary.id}>
                      <AlertTriangle size={14} />
                      {summary.reviewCount}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {activeTake && (
              <div className="composition-take-detail">
                <TextField
                  size="small"
                  label="Take name"
                  value={draftName}
                  inputProps={{ 'data-take-name-input': true, maxLength: 80 }}
                  onChange={(event) => setDraftName(event.target.value)}
                  onBlur={() => {
                    const next = draftName.trim();
                    if (next && next !== activeTake.name) onRename(activeTake.id, next);
                  }}
                />

                <div className="composition-take-actions" role="toolbar" aria-label="Take playback">
                  {audition === 'idle' ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<Play size={16} />}
                        data-take-play-original
                        disabled={!audioAvailable && activeTake.source === 'audio'}
                        onClick={() => onPlayOriginal(activeTake)}
                      >
                        Original performance
                      </Button>
                      <Button
                        startIcon={<Play size={16} />}
                        data-take-play-edited
                        onClick={() => onPlayEdited(activeTake)}
                      >
                        Edited score
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<Square size={16} />}
                      data-take-stop
                      onClick={onStop}
                    >
                      Stop {audition === 'original' ? 'original' : 'edited score'}
                    </Button>
                  )}
                  <Button
                    startIcon={<Repeat size={16} />}
                    data-take-replace
                    onClick={() => onReplaceWithNewTake(activeTake)}
                  >
                    Play this passage again
                  </Button>
                </div>

                {activeTake.source === 'audio' && !activeTake.audio && (
                  <p className="composition-muted" data-take-audio-missing>
                    The original audio was not kept for this take. The recognized notes are still
                    here, and the edited score plays back normally.
                  </p>
                )}
                {activeTake.audio && !audioAvailable && (
                  <p className="composition-muted" data-take-audio-elsewhere>
                    The original recording stays on the device that captured it, so it cannot be
                    played here.
                  </p>
                )}
                {activeTake.audio && audioAvailable && (
                  <Button
                    size="small"
                    color="warning"
                    data-take-delete-audio
                    onClick={() => onDeleteAudio(activeTake)}
                  >
                    Delete original recording
                  </Button>
                )}

                <h4 className="composition-take-heading">
                  {reviewNotes.length === 0
                    ? 'Nothing flagged for review'
                    : reviewNotes.length === 1
                      ? '1 note to review'
                      : `${reviewNotes.length} notes to review`}
                </h4>
                {activeTake.source === 'midi' ? (
                  <p className="composition-muted">
                    This take came from a MIDI instrument, which reported each pitch exactly. There
                    is nothing for recognition to have got wrong.
                  </p>
                ) : reviewNotes.length === 0 ? (
                  <p className="composition-muted">
                    Every note in this take was recognized confidently. Listen to the original to
                    check anything that still sounds different.
                  </p>
                ) : (
                  <ul className="composition-review-list">
                    {reviewNotes.map((reviewNote) => (
                      <li key={reviewNote.note.id}>
                        <button
                          type="button"
                          data-take-review-note={reviewNote.note.id}
                          onClick={() => onJumpToNote(reviewNote)}
                        >
                          <span className="composition-review-pitch">
                            {describeMidiNote(reviewNote.note.midi)}
                          </span>
                          <span className="composition-muted">
                            {formatDuration(reviewNote.note.startedAtMs)} ·{' '}
                            {Math.round((reviewNote.note.confidence ?? 0) * 100)}% match
                            {reviewNote.eventId ? '' : ' · no longer in the score'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
        {message && (
          <p className="composition-take-message" data-take-review-message role="status">
            {message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
